import type { EnrichedAccessory } from 'components/layout/modals/compatible-combinations-browser-modal';
import { parseAccessoriesString } from 'utils/combination-pricing';

export type AccessoryPick = {
  accessoryId: number;
  typeId: number;
};

export type AccessoryOption = AccessoryPick & {
  enriched: EnrichedAccessory;
};

export type AccessoryGroup = {
  accessoryTypeCode: string;
  accessoryTypeLabel: string;
  options: AccessoryOption[];
};

export type AccessoryPicks = Record<string, AccessoryPick>;

export const accessoryPairKey = ({ accessoryId, typeId }: AccessoryPick) =>
  `${accessoryId}*${typeId}`;

export const isSameAccessoryPick = (
  left: AccessoryPick,
  right: AccessoryPick
) => left.accessoryId === right.accessoryId && left.typeId === right.typeId;

export const toAccessoryPick = ({
  accessoryId,
  typeId
}: AccessoryPick): AccessoryPick => ({ accessoryId, typeId });

function uniqueAccessoryPairs(
  accessories: string | null | undefined
): AccessoryPick[] {
  const seen = new Set<string>();
  return parseAccessoriesString(accessories).filter((pair) => {
    const key = accessoryPairKey(pair);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const compareNullableAscending = (
  left: number | null | undefined,
  right: number | null | undefined
): number => {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return left - right;
};

export const sortAccessoryOptions = (
  options: AccessoryOption[]
): AccessoryOption[] =>
  [...options].sort(
    (left, right) =>
      compareNullableAscending(
        left.enriched.heatingCapacityBtu,
        right.enriched.heatingCapacityBtu
      ) ||
      compareNullableAscending(
        left.enriched.product?.price,
        right.enriched.product?.price
      )
  );

export const hasActiveProduct = ({
  enriched: { product }
}: AccessoryOption): boolean => product?.active === true;

export const offeredAccessoryOptions = (
  options: AccessoryOption[]
): AccessoryOption[] => {
  const active = options.filter(hasActiveProduct);
  return sortAccessoryOptions(active.length > 0 ? active : options);
};

export function groupCombinationAccessories(
  accessories: string | null | undefined,
  accessoryData: Record<string, EnrichedAccessory>
): AccessoryGroup[] {
  const groups = new Map<string, AccessoryGroup>();
  uniqueAccessoryPairs(accessories).forEach((pair) => {
    const enriched = accessoryData[accessoryPairKey(pair)];
    if (!enriched) return;
    const group = groups.get(enriched.accessoryTypeCode) ?? {
      accessoryTypeCode: enriched.accessoryTypeCode,
      accessoryTypeLabel: enriched.accessoryTypeLabel,
      options: []
    };
    group.options.push({ ...pair, enriched });
    groups.set(enriched.accessoryTypeCode, group);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    options: offeredAccessoryOptions(group.options)
  }));
}

export const hasAccessoryChoice = (group: AccessoryGroup) =>
  group.options.length > 1;

export const enrichedAccessoryModelText = (enriched: EnrichedAccessory) =>
  [enriched.manufacturer, enriched.modelNumber].filter(Boolean).join(' ');

export const accessoryOptionModelText = (option: AccessoryOption) =>
  enrichedAccessoryModelText(option.enriched);

export const isAccessoryOptionInStock = ({
  enriched: { product }
}: AccessoryOption): boolean =>
  product != null && product.active && product.fulfillableStock > 0;

export function recommendedAccessoryOption(
  group: AccessoryGroup
): AccessoryOption {
  return group.options.reduce((cheapest, option) => {
    const price = option.enriched.product?.price;
    const cheapestPrice = cheapest.enriched.product?.price;
    if (price == null) return cheapest;
    if (cheapestPrice == null || price < cheapestPrice) return option;
    return cheapest;
  });
}

export function defaultAccessoryOption(group: AccessoryGroup): AccessoryOption {
  const recommended = recommendedAccessoryOption(group);
  if (isAccessoryOptionInStock(recommended)) return recommended;
  const larger = group.options.slice(group.options.indexOf(recommended) + 1);
  return larger.find(isAccessoryOptionInStock) ?? recommended;
}

export function selectedAccessoryOption(
  group: AccessoryGroup,
  picks: AccessoryPicks
): AccessoryOption {
  const pick = picks[group.accessoryTypeCode];
  const picked = pick
    ? group.options.find((option) => isSameAccessoryPick(option, pick))
    : undefined;
  return picked ?? defaultAccessoryOption(group);
}

export const quotableAccessoryProductIds = (system: {
  quotableAccessories: ReadonlyArray<{ productId: number }>;
}): ReadonlySet<number> =>
  new Set(system.quotableAccessories.map((accessory) => accessory.productId));

export function picksByTypeCode(
  picks: ReadonlyArray<AccessoryPick>,
  accessoryData: Record<string, EnrichedAccessory>
): AccessoryPicks {
  return Object.fromEntries(
    picks.flatMap((pick) => {
      const enriched = accessoryData[accessoryPairKey(pick)];
      return enriched
        ? [[enriched.accessoryTypeCode, toAccessoryPick(pick)]]
        : [];
    })
  );
}

export function picksMatchingProductIds(
  groups: AccessoryGroup[],
  productIds: ReadonlySet<number>
): AccessoryPicks {
  return Object.fromEntries(
    groups.filter(hasAccessoryChoice).flatMap((group) => {
      const match = group.options.find((option) =>
        productIds.has(option.enriched.alpineProductId)
      );
      return match ? [[group.accessoryTypeCode, toAccessoryPick(match)]] : [];
    })
  );
}

export function resolveAccessoriesString(
  accessories: string | null | undefined,
  accessoryData: Record<string, EnrichedAccessory>,
  picks: AccessoryPicks
): string | null {
  const selectedKeys = new Set(
    groupCombinationAccessories(accessories, accessoryData).map((group) =>
      accessoryPairKey(selectedAccessoryOption(group, picks))
    )
  );
  const pairs = uniqueAccessoryPairs(accessories)
    .map(accessoryPairKey)
    .filter((key) => accessoryData[key] === undefined || selectedKeys.has(key));
  return pairs.length > 0 ? pairs.join(',') : null;
}
